import { Text } from '@televet/kibble-ui/build/components/Text';
import { Modal } from '@televet/kibble-ui/build/components/Modal';
import { ModalBody } from '@televet/kibble-ui/build/components/Modal';
import { ModalCloseButton } from '@televet/kibble-ui/build/components/Modal';
import { ModalFooter } from '@televet/kibble-ui/build/components/Modal';
import { ModalHeader } from '@televet/kibble-ui/build/components/Modal';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { AppointmentNoteTemplateGlobalId } from '@televet/shared-types/recap/enums/AppointmentNoteTemplateGlobalId';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useCreateOneAppointmentNoteTemplateMutation,
  useFindUniqueAppointmentNoteTemplateQuery,
  useUpdateOneAppointmentNoteTemplateMutation,
} from 'shared/types/graphql';
import withLazyModal from '../LazyModal/withLazyModal';
import DeleteTemplateModal from './DeleteTemplateModal';
import { Box, Flex, useDisclosure } from '@televet/kibble-ui/build/chakra';

const CUSTOM_TEMPLATE_NAME_PLACEHOLDER = 'Routine Wellness Visit ';

const CUSTOM_TEMPLATE_PLACEHOLDER = `Patient:
Species: 
Age:
Weight: (weight in lbs) 

Chief Complaint:


Physical Exam:
- Temperature: 
- Heart Rate: 
- Respiratory 

Assessment:


Plan:

`;

type CreateOrEditTemplateModalProps = {
  templateId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  refetchTemplates: () => void;
  setTemplateId: (id: string) => void;
};

const CreateOrEditTemplateModal = ({
  templateId,
  isOpen,
  onClose,
  refetchTemplates,
  setTemplateId,
}: CreateOrEditTemplateModalProps): JSX.Element => {
  const {
    onOpen: onDeleteTemplateOpen,
    onClose: onDeleteTemplateClose,
    isOpen: isDeleteTemplateOpen,
  } = useDisclosure();
  const { clinicUserId, currentClinicId } = useClinicUser();
  const [templateName, setTemplateName] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [errorMessages, setErrorMessages] = useState({
    templateName: '',
    templateBody: '',
  });

  const { data: templateData, loading: isTemplateDataLoading } = useFindUniqueAppointmentNoteTemplateQuery({
    variables: {
      where: {
        id: templateId,
      },
    },
    skip: templateId === AppointmentNoteTemplateGlobalId.NEW || !templateId,
    fetchPolicy: GraphQLFetchPolicies.NoCache,
  });

  const handleMutationSuccess = (id?: string): void => {
    setErrorMessages({
      templateName: '',
      templateBody: '',
    });
    refetchTemplates();
    setTemplateId(id ?? AppointmentNoteTemplateGlobalId.SOAP);
    onClose();
  };

  const [createNoteTemplate, { loading: isCreateNoteTemplateLoading }] = useCreateOneAppointmentNoteTemplateMutation({
    onCompleted: (data) => {
      handleMutationSuccess(data.createOneAppointmentNoteTemplate.id);
    },
  });

  const [updateNoteTemplate, { loading: isUpdateNoteTemplateLoading }] = useUpdateOneAppointmentNoteTemplateMutation({
    onCompleted: (data) => {
      handleMutationSuccess(data.updateOneAppointmentNoteTemplate.id);
    },
  });

  const [deleteNoteTemplate, { loading: isDeleteNoteTemplateLoading }] = useUpdateOneAppointmentNoteTemplateMutation({
    onCompleted: () => {
      handleMutationSuccess();
      onDeleteTemplateClose();
    },
  });

  useEffect(() => {
    if (templateData && templateData.findUniqueAppointmentNoteTemplate) {
      setTemplateName(templateData.findUniqueAppointmentNoteTemplate.name);
      setTemplateBody(templateData.findUniqueAppointmentNoteTemplate.body);
    }
  }, [templateData]);

  const modalHeader = useMemo(() => {
    if (templateId === AppointmentNoteTemplateGlobalId.NEW) {
      return 'Create Custom Template';
    }
    return 'Edit Custom Template';
  }, [templateId]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setTemplateName(e.target.value);
  }, []);

  const handleBodyChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setTemplateBody(e.target.value);
  }, []);

  const handleSaveTemplate = (): void => {
    try {
      if (templateName && templateBody) {
        if (templateId === AppointmentNoteTemplateGlobalId.NEW) {
          createNoteTemplate({
            variables: {
              data: {
                name: templateName,
                body: templateBody,
                clinic: { connect: { id: currentClinicId } },
                updatedBy: { connect: { id: clinicUserId } },
              },
            },
          });
        } else {
          // update template
          updateNoteTemplate({
            variables: {
              where: {
                id: templateId,
              },
              data: {
                name: templateName,
                body: templateBody,
                updatedBy: { connect: { id: clinicUserId } },
              },
            },
          });
        }
      } else {
        if (!templateName) {
          setErrorMessages((prev) => {
            return { ...prev, templateName: 'Template name is required' };
          });
        } else {
          setErrorMessages((prev) => {
            return { ...prev, templateName: '' };
          });
        }
        if (!templateBody) {
          setErrorMessages((prev) => {
            return { ...prev, templateBody: 'Template body is required' };
          });
        } else {
          setErrorMessages((prev) => {
            return { ...prev, templateBody: '' };
          });
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const isTemplateGlobal = useMemo(() => {
    return templateId === AppointmentNoteTemplateGlobalId.SOAP || templateId === AppointmentNoteTemplateGlobalId.NEW;
  }, [templateId]);

  const handleCloseModal = (): void => {
    setTemplateName('');
    setTemplateBody('');
    onClose();
    const newTemplateId = templateId === AppointmentNoteTemplateGlobalId.NEW ? undefined : templateId;
    setTemplateId(newTemplateId ?? AppointmentNoteTemplateGlobalId.SOAP);
  };

  const handleDeleteTemplate = useCallback(
    (templateId: string): void => {
      try {
        if (templateId === AppointmentNoteTemplateGlobalId.NEW || templateId === AppointmentNoteTemplateGlobalId.SOAP) {
          return;
        }
        deleteNoteTemplate({
          variables: {
            where: {
              id: templateId,
            },
            data: {
              isDeleted: true,
            },
          },
        });
        setTemplateId(AppointmentNoteTemplateGlobalId.SOAP);
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    },
    [setTemplateId, deleteNoteTemplate],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} size="3xl">
      <>
        <ModalHeader>{modalHeader}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <TextInput
            value={templateName}
            label="Template Name"
            placeholder={CUSTOM_TEMPLATE_NAME_PLACEHOLDER}
            isRequired
            onChange={handleNameChange}
            isInvalid={!!errorMessages.templateName}
            errorText={errorMessages.templateName}
          />
          <Box mt={5} mb={1}>
            <Text as="p">
              Template Body <Text variant="danger">*</Text>
            </Text>
          </Box>
          <Textarea
            rows={15}
            value={templateBody}
            label="For best results, use a template that matches the structure of your ideal output."
            placeholder={CUSTOM_TEMPLATE_PLACEHOLDER}
            onChange={handleBodyChange}
            isInvalid={!!errorMessages.templateBody}
            errorText={errorMessages.templateBody}
          />
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="space-between" width="100%">
            <Button variant="tertiary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Flex gap={4}>
              {!isTemplateGlobal && (
                <Button
                  isLoading={
                    isCreateNoteTemplateLoading ||
                    isUpdateNoteTemplateLoading ||
                    isDeleteNoteTemplateLoading ||
                    isTemplateDataLoading
                  }
                  variant="primaryDestructive"
                  onClick={onDeleteTemplateOpen}
                >
                  Delete
                </Button>
              )}
              <Button
                isLoading={
                  isCreateNoteTemplateLoading ||
                  isUpdateNoteTemplateLoading ||
                  isDeleteNoteTemplateLoading ||
                  isTemplateDataLoading
                }
                onClick={handleSaveTemplate}
              >
                Save Template
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
        <DeleteTemplateModal
          onClose={onDeleteTemplateClose}
          isOpen={isDeleteTemplateOpen}
          templateId={templateId}
          templateName={templateName}
          handleDeleteTemplate={handleDeleteTemplate}
        />
      </>
    </Modal>
  );
};

export default withLazyModal(CreateOrEditTemplateModal);
